import React, { useCallback, useEffect, useMemo, useState } from "react";

import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import WebAssetOutlinedIcon from "@mui/icons-material/WebAssetOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";

import { useSelector } from "react-redux";

import InfoTooltip from "../../../../components/molecules/InfoTooltip";
import SwalAlertComp from "../../../../components/SwalAlert";
import {
    getAttributionPolicy,
    updateAttributionPolicy,
} from "../../../../helpers/trackers/trackerSettings";
import {
    ActionBarInner,
    GeneralSettingsWrapper,
    KeywordInputRow,
    KeywordsCard,
    KeywordsChips,
    KeywordsLabel,
    LoadingWrapper,
    PageDescription,
    PolicyCard,
    PolicyHint,
    PolicyRow,
    PolicySelectLabel,
    PolicySelectWrapper,
    RadioCard,
    RadioCardDescription,
    RadioCardGrid,
    RadioCardHeader,
    RadioCardIcon,
    RadioCardTitle,
    RadioIndicator,
    RadioIndicatorDot,
    SectionBlock,
    SectionHeaderRow,
    SectionTitle,
    StickyActionBar,
    StyledKeywordChip,
    StyledKeywordInput,
} from "./GeneralSettings.styled";

const ATT_POLICY_OPTIONS = [
    { value: "deny", label: "Deny" },
    { value: "approve", label: "Approve" },
    { value: "anonymize", label: "Anonymize" },
];

const ATT_POLICY_HINTS = {
    approve: {
        label: "Automatic Approval:",
        text: "Signals meeting specified threshold parameters will be instantly attributed to the designated project dashboard without manual intervention.",
    },
    deny: {
        label: "Deny Policy:",
        text: "Unattributed signals will be rejected and will not appear in any project dashboard or analytics view.",
    },
    anonymize: {
        label: "Anonymize Policy:",
        text: "Unattributed signals will be recorded without personal identifiers, preserving aggregate analytics while protecting user privacy.",
    },
};

const DEFAULT_KEYWORDS = ["w2a", "signalsight", "zzg", "web2app", "Webtoapp"];

const GeneralSettings = ({ trackerId }) => {
    const language = useSelector((state) => state.languagesState.languages);

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [onlyAttributed, setOnlyAttributed] = useState(null);
    const [attPolicy, setAttPolicy] = useState("approve");
    const [keywords, setKeywords] = useState([...DEFAULT_KEYWORDS]);
    const [keywordInput, setKeywordInput] = useState("");
    const [isNewSetting, setIsNewSetting] = useState(true);

    const [originalOnlyAttributed, setOriginalOnlyAttributed] = useState(null);
    const [originalAttPolicy, setOriginalAttPolicy] = useState(null);
    const [originalKeywords, setOriginalKeywords] = useState([
        ...DEFAULT_KEYWORDS,
    ]);

    const fetchSettings = useCallback(async () => {
        if (!trackerId) return;

        setIsLoading(true);
        try {
            const response = await getAttributionPolicy(trackerId);
            const hasValidData =
                response.status &&
                response.data &&
                !Array.isArray(response.data) &&
                typeof response.data === "object" &&
                "only_attributed" in response.data;

            if (hasValidData) {
                const {
                    only_attributed,
                    att_policy,
                    keywords: savedKeywords,
                } = response.data;

                setOnlyAttributed(only_attributed);
                setOriginalOnlyAttributed(only_attributed);
                setIsNewSetting(false);

                setAttPolicy(att_policy || "approve");
                setOriginalAttPolicy(att_policy || "approve");

                if (
                    savedKeywords &&
                    Array.isArray(savedKeywords) &&
                    savedKeywords.length > 0
                ) {
                    const merged = [
                        ...new Set([...DEFAULT_KEYWORDS, ...savedKeywords]),
                    ];
                    setKeywords(merged);
                    setOriginalKeywords(merged);
                } else {
                    setKeywords([...DEFAULT_KEYWORDS]);
                    setOriginalKeywords([...DEFAULT_KEYWORDS]);
                }
            } else {
                setIsNewSetting(true);
                setOnlyAttributed(null);
                setAttPolicy("approve");
                setOriginalOnlyAttributed(null);
                setOriginalAttPolicy(null);
                setKeywords([...DEFAULT_KEYWORDS]);
                setOriginalKeywords([...DEFAULT_KEYWORDS]);
            }
        } catch (error) {
            setIsNewSetting(true);
            setOnlyAttributed(null);
            setAttPolicy("approve");
            setOriginalOnlyAttributed(null);
            setOriginalAttPolicy(null);
            setKeywords([...DEFAULT_KEYWORDS]);
            setOriginalKeywords([...DEFAULT_KEYWORDS]);
        } finally {
            setIsLoading(false);
        }
    }, [trackerId]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const canSave = useMemo(() => {
        const isFormValid = onlyAttributed !== null && !!attPolicy && !isSaving;
        if (!isFormValid) return false;
        if (isNewSetting) return true;

        const keywordsChanged =
            onlyAttributed === true &&
            JSON.stringify([...keywords].sort()) !==
                JSON.stringify([...originalKeywords].sort());

        return (
            onlyAttributed !== originalOnlyAttributed ||
            attPolicy !== originalAttPolicy ||
            keywordsChanged
        );
    }, [
        onlyAttributed,
        attPolicy,
        keywords,
        originalOnlyAttributed,
        originalAttPolicy,
        originalKeywords,
        isNewSetting,
        isSaving,
    ]);

    const handleAddKeyword = () => {
        const trimmed = keywordInput.trim();
        if (!trimmed) return;
        if (keywords.some((k) => k.toLowerCase() === trimmed.toLowerCase())) {
            setKeywordInput("");
            return;
        }
        setKeywords((prev) => [...prev, trimmed]);
        setKeywordInput("");
    };

    const handleKeywordKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddKeyword();
        }
    };

    const handleRemoveKeyword = (keyword) => {
        if (DEFAULT_KEYWORDS.includes(keyword)) return;
        setKeywords((prev) => prev.filter((k) => k !== keyword));
    };



    const handleSave = async () => {
        if (!trackerId || !canSave) return;

        setIsSaving(true);
        try {
            const data = {
                only_attributed: onlyAttributed,
                att_policy: attPolicy,
            };

            if (onlyAttributed === true) {
                data.keywords = keywords;
            }

            const response = await updateAttributionPolicy(trackerId, data);

            if (response.status) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.success ?? "Success",
                    message: "Settings saved successfully",
                    icon: "success",
                });

                setOriginalOnlyAttributed(onlyAttributed);
                setOriginalAttPolicy(attPolicy);
                setOriginalKeywords([...keywords]);
                setIsNewSetting(false);
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message:
                        response.message ??
                        language?.general_settings?.save_error ??
                        "Failed to save settings",
                    icon: "error",
                });
            }
        } catch (error) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message:
                    language?.general_settings?.save_error ??
                    "Failed to save settings",
                icon: "error",
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <LoadingWrapper>
                <CircularProgress size={24} />
            </LoadingWrapper>
        );
    }

    const currentHint = ATT_POLICY_HINTS[attPolicy] || ATT_POLICY_HINTS.approve;

    return (
        <GeneralSettingsWrapper>
            {/* Signal Collection Method */}
            <SectionBlock>
                <SectionHeaderRow>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexDirection: "column",
                        }}
                    >
                        <SectionTitle>
                            {language?.general_settings
                                ?.signal_collection_title ??
                                "Signal Collection Method"}
                        </SectionTitle>
                        <Box>
                            Choose how signals are collected for your Web2App
                            (W2A) landing page campaign.
                        </Box>
                    </Box>
                </SectionHeaderRow>

                <RadioCardGrid>
                    <RadioCard
                        $active={onlyAttributed === false}
                        onClick={() => setOnlyAttributed(false)}
                    >
                        <input
                            type="radio"
                            name="signal-source"
                            checked={onlyAttributed === false}
                            readOnly
                        />
                        <RadioCardHeader>
                            <RadioCardIcon $active={onlyAttributed === false}>
                                <CampaignOutlinedIcon />
                            </RadioCardIcon>
                            <RadioIndicator $active={onlyAttributed === false}>
                                <RadioIndicatorDot
                                    $active={onlyAttributed === false}
                                />
                            </RadioIndicator>
                        </RadioCardHeader>
                        <RadioCardTitle>
                            {language?.general_settings?.all_campaigns_title ??
                                "All Campaigns"}
                        </RadioCardTitle>
                        <RadioCardDescription variant="body2">
                            {language?.general_settings
                                ?.all_campaigns_description ??
                                "Track signals from all active advertising and organic search campaigns universally."}
                        </RadioCardDescription>
                    </RadioCard>

                    <RadioCard
                        $active={onlyAttributed === true}
                        onClick={() => setOnlyAttributed(true)}
                    >
                        <input
                            type="radio"
                            name="signal-source"
                            checked={onlyAttributed === true}
                            readOnly
                        />
                        <RadioCardHeader>
                            <RadioCardIcon $active={onlyAttributed === true}>
                                <WebAssetOutlinedIcon />
                            </RadioCardIcon>
                            <RadioIndicator $active={onlyAttributed === true}>
                                <RadioIndicatorDot
                                    $active={onlyAttributed === true}
                                />
                            </RadioIndicator>
                        </RadioCardHeader>
                        <RadioCardTitle>
                            {language?.general_settings
                                ?.only_landing_page_title ??
                                "Only Landing Page Campaign"}
                        </RadioCardTitle>
                        <RadioCardDescription variant="body2">
                            {language?.general_settings
                                ?.only_landing_page_description ??
                                "Isolate signals strictly originating from specific landing page triggers and targeted keywords."}
                        </RadioCardDescription>
                    </RadioCard>
                </RadioCardGrid>
            </SectionBlock>

            {/* Keywords */}
            {onlyAttributed === true && (
                <SectionBlock>
                    <SectionTitle sx={{ mb: "4px" }}>
                        {language?.general_settings?.keywords_title ??
                            "Keywords"}
                    </SectionTitle>
                    <Box sx={{ mb: "20px" }}>
                        When Only Landing Page Campaign is selected, the system
                        uses keywords to identify landing page campaigns. Some
                        keywords are provided by default. You can use these
                        predefined keywords or add your own tracker name or
                        custom keywords. If a campaign name contains any of
                        these keywords, it will be automatically detected as a
                        landing page campaign.
                    </Box>
                    <Box mb="28px">
                        <KeywordsLabel>
                            {language?.general_settings
                                ?.keywords_filter_label ??
                                "Active Keyword Filters"}
                        </KeywordsLabel>
                        <KeywordsChips>
                            {keywords.map((keyword) => {
                                const isDefault =
                                    DEFAULT_KEYWORDS.includes(keyword);
                                return (
                                    <StyledKeywordChip
                                        key={keyword}
                                        label={keyword}
                                        onDelete={
                                            isDefault
                                                ? undefined
                                                : () =>
                                                      handleRemoveKeyword(
                                                          keyword,
                                                      )
                                        }
                                        color={"primary"}
                                        variant={
                                            isDefault ? "filled" : "outlined"
                                        }
                                        size="small"
                                    />
                                );
                            })}
                        </KeywordsChips>
                    </Box>

                    <KeywordInputRow>
                        <StyledKeywordInput
                            size="small"
                            variant="outlined"
                            placeholder={
                                language?.general_settings
                                    ?.keyword_placeholder ?? "Add a keyword"
                            }
                            value={keywordInput}
                            onChange={(e) => setKeywordInput(e.target.value)}
                            onKeyDown={handleKeywordKeyDown}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAddKeyword}
                            disabled={!keywordInput.trim()}
                        >
                            {language?.buttons?.add ?? "Add"}
                        </Button>
                    </KeywordInputRow>
                </SectionBlock>
            )}

            {/* Attribution Policy */}
            {onlyAttributed !== null && (
                <SectionBlock>
                    <SectionTitle sx={{ mb: "20px" }}>
                        {language?.general_settings?.att_policy_label ??
                            "Attribution Policy"}
                    </SectionTitle>

                    <PolicyCard elevation={0}>
                        <PolicyRow>
                            <PolicySelectWrapper size="small">
                                <PolicySelectLabel>
                                    {language?.general_settings
                                        ?.policy_action_label ??
                                        "Policy Action"}
                                </PolicySelectLabel>
                                <Select
                                    value={attPolicy}
                                    onChange={(e) =>
                                        setAttPolicy(e.target.value)
                                    }
                                    fullWidth
                                    size="small"
                                >
                                    {ATT_POLICY_OPTIONS.map((option) => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {language?.general_settings?.[
                                                `att_policy_${option.value}`
                                            ] ?? option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </PolicySelectWrapper>

                            <PolicyHint>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "#586065",
                                        lineHeight: 1.6,
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            fontWeight: 700,
                                            color: "#2b3438",
                                        }}
                                    >
                                        {language?.general_settings?.[
                                            `att_hint_label_${attPolicy}`
                                        ] ?? currentHint.label}
                                    </Box>{" "}
                                    {language?.general_settings
                                        ?.att_policy_hint ?? currentHint.text}
                                </Typography>
                            </PolicyHint>
                        </PolicyRow>
                    </PolicyCard>
                </SectionBlock>
            )}

            {/* Sticky Action Bar */}
            <StickyActionBar>
                <ActionBarInner>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={!canSave}
                        startIcon={
                            isSaving && (
                                <CircularProgress size={18} color="inherit" />
                            )
                        }
                    >
                        {isSaving
                            ? (language?.buttons?.saving ?? "Saving...")
                            : (language?.buttons?.save ?? "Save Changes")}
                    </Button>
                </ActionBarInner>
            </StickyActionBar>
        </GeneralSettingsWrapper>
    );
};

export default GeneralSettings;
